import { useRef, useCallback, useState, useEffect } from 'react';
import ModalDialog from './common/ModalDialog';
import { TrashIcon, PencilSquareIcon } from '@heroicons/react/24/outline';

export default function DevCapacityTable({ developers, weeks, totals, onRemove, onEdit, onCapacityChange, removing, isFullScreen = false, resetVersions = {} }) {
  const { perWeek, totalProjectCapacity, devPerWeek, qaPerWeek, totalDevCapacity, totalQaCapacity } = totals;
  const debounceTimers = useRef({});
  const [devToRemove, setDevToRemove] = useState(null);
  const [localValues, setLocalValues] = useState({});

  const prevResetVersionsRef = useRef({});

  useEffect(() => {
    setLocalValues(prev => {
      const next = { ...prev };
      developers.forEach(dev => {
        const currentVersion = resetVersions[dev.id] ?? 0;
        const previousVersion = prevResetVersionsRef.current[dev.id] ?? 0;
        const shouldForceRefresh = currentVersion !== previousVersion;

        (dev.weeklyHours ?? []).forEach((h, wi) => {
          const key = `${dev.id}-${wi}`;
          // Force refresh this dev's cells if their version bumped (e.g. after edit save);
          // otherwise don't overwrite in-progress user edits.
          if (shouldForceRefresh || !(key in prev)) {
            next[key] = parseFloat((Number(h) || 0).toFixed(2));
          }
        });

        prevResetVersionsRef.current[dev.id] = currentVersion;
      });
      return next;
    });
  }, [developers, resetVersions]);
  
  const handleHoursChange = useCallback((devId, weekIndex, value) => {
    const key = `${devId}-${weekIndex}`;
    // Keep raw string while typing so user can type decimals freely
    setLocalValues(prev => ({ ...prev, [key]: value }));

    const parsed = parseFloat(value);
    const hours = isNaN(parsed) ? 0 : Math.max(0, parseFloat(parsed.toFixed(2)));

    const timerKey = key;
    if (debounceTimers.current[timerKey]) {
      clearTimeout(debounceTimers.current[timerKey]);
    }

    debounceTimers.current[timerKey] = setTimeout(() => {
      onCapacityChange?.(devId, weekIndex, hours);
      delete debounceTimers.current[timerKey];
    }, 500);
  }, [onCapacityChange]);


  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach(clearTimeout);
    };
  }, []);

  const shadowClass = 'shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] dark:shadow-[2px_0_4px_-2px_rgba(0,0,0,0.3)]';

  return (
    <>
      <div
        className="overflow-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm"
        style={{ maxHeight: isFullScreen ? 'calc(100vh - 140px)' : 'calc(100vh - 180px)' }}
      >
        <table className="dev-capacity-table w-full min-w-[600px] border-collapse">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
              <th className="sticky left-0 z-20 min-w-[140px] bg-slate-50 dark:bg-slate-900 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Team member
              </th>
              <th className="sticky left-[140px] z-20 min-w-[100px] bg-slate-50 dark:bg-slate-900 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Role
              </th>
              <th className="sticky left-[240px] z-20 min-w-[100px] bg-slate-50 dark:bg-slate-900 px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Total hrs
              </th>
              <th className={`sticky left-[340px] z-20 min-w-[88px] w-[88px] bg-slate-50 dark:bg-slate-900 px-2 py-3 ${shadowClass}`} />
              {weeks.map((w, i) => (
                <th
                  key={i}
                  className="min-w-[90px] px-3 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap"
                >
                  <span className="block text-[10px] text-slate-500 dark:text-slate-400">Week</span>
                  {w.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {developers.map((dev) => {
              const totalHrs = (dev.weeklyHours ?? []).reduce((a, b) => a + (b ?? 0), 0);
              return (
                <tr key={dev.id} className="group hover:bg-slate-50 dark:hover:bg-slate-700 focus-within:bg-indigo-50 dark:focus-within:bg-slate-700 transition-colors">
                  <td className="sticky left-0 z-20 bg-white group-hover:bg-slate-50 group-focus-within:bg-indigo-50 dark:bg-slate-800 dark:group-hover:bg-slate-700 dark:group-focus-within:bg-slate-700 px-4 py-3 text-sm font-medium text-slate-900 dark:text-white dark:group-focus-within:text-indigo-300 transition-colors">
                    {dev.name}
                  </td>
                  <td className="sticky left-[140px] z-20 bg-white group-hover:bg-slate-50 group-focus-within:bg-indigo-50 dark:bg-slate-800 dark:group-hover:bg-slate-700 dark:group-focus-within:bg-slate-700 px-4 py-3 text-sm text-slate-600 dark:text-slate-300 transition-colors">
                    {dev.role}
                  </td>
                  <td className="sticky left-[240px] z-20 bg-white group-hover:bg-slate-50 group-focus-within:bg-indigo-50 dark:bg-slate-800 dark:group-hover:bg-slate-700 dark:group-focus-within:bg-slate-700 px-4 py-3 text-right text-sm font-medium text-slate-700 dark:text-slate-200 tabular-nums transition-colors">
                    {totalHrs.toFixed(2)}
                  </td>
                  <td className={`sticky left-[340px] z-20 w-[88px] bg-white group-hover:bg-slate-50 group-focus-within:bg-indigo-50 dark:bg-slate-800 dark:group-hover:bg-slate-700 dark:group-focus-within:bg-slate-700 px-2 py-2 transition-colors ${shadowClass}`}>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => onEdit?.(dev)}
                        className="rounded p-1.5 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600 dark:hover:bg-indigo-900/30 dark:hover:text-indigo-400 transition-colors"
                        title="Edit team member"
                      >
                        <PencilSquareIcon className="size-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDevToRemove(dev)}
                        className="rounded p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-900/30 dark:hover:text-rose-400 transition-colors"
                        title="Remove team member"
                      >
                        <TrashIcon className="size-4" />
                      </button>
                    </div>
                  </td>
                  {weeks.map((_, wi) => (
                    <td key={wi} className="px-2 py-2">
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        value={localValues[`${dev.id}-${wi}`] ?? parseFloat((Number(dev.weeklyHours?.[wi]) || 0).toFixed(2))}
                        onChange={(e) => handleHoursChange(dev.id, wi, e.target.value)}
                        onBlur={(e) => {
                          const key = `${dev.id}-${wi}`;
                          const parsed = parseFloat(e.target.value);
                          const normalized = isNaN(parsed) ? 0 : Math.max(0, parseFloat(parsed.toFixed(2)));
                          setLocalValues(prev => ({ ...prev, [key]: normalized }));
                        }}
                        className="w-full min-w-[60px] rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm text-slate-900 dark:text-white text-center tabular-nums focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
            {developers.length === 0 && (
              <tr>
                <td colSpan={4 + weeks.length} className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  No team members added yet. Click "Add team member" to get started.
                </td>
              </tr>
            )}
            {developers.length > 0 && (
              <>
                {/* Total Dev Capacity row */}
                <tr className="border-t border-slate-200 dark:border-slate-700 bg-blue-50 dark:bg-blue-900/20">
                  <td colSpan={2} className="sticky left-0 z-20 bg-blue-50 dark:bg-blue-900/20 px-4 py-2.5 text-sm font-medium text-blue-700 dark:text-blue-300">
                    Total Dev Capacity
                  </td>
                  <td className="sticky left-[240px] z-20 bg-blue-50 dark:bg-blue-900/20 px-4 py-2.5 text-right text-sm font-medium text-blue-700 dark:text-blue-300 tabular-nums">
                    {(Number(totalDevCapacity) || 0).toFixed(2)}
                  </td>
                  <td className={`sticky left-[340px] z-20 w-[88px] bg-blue-50 dark:bg-blue-900/20 px-2 py-2.5 ${shadowClass}`} />
                  {weeks.map((_, wi) => (
                    <td key={wi} className="px-2 py-2.5 text-center text-sm text-blue-700 dark:text-blue-300 tabular-nums">
                      {(Number(devPerWeek[wi]) || 0).toFixed(2)}
                    </td>
                  ))}
                </tr>

                {/* Total QA Capacity row */}
                <tr className="border-t border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20">
                  <td colSpan={2} className="sticky left-0 z-20 bg-purple-50 dark:bg-purple-900/20 px-4 py-2.5 text-sm font-medium text-purple-700 dark:text-purple-300">
                    Total QA Capacity
                  </td>
                  <td className="sticky left-[240px] z-20 bg-purple-50 dark:bg-purple-900/20 px-4 py-2.5 text-right text-sm font-medium text-purple-700 dark:text-purple-300 tabular-nums">
                    {(Number(totalQaCapacity) || 0).toFixed(2)}
                  </td>
                  <td className={`sticky left-[340px] z-20 w-[88px] bg-purple-50 dark:bg-purple-900/20 px-2 py-2.5 ${shadowClass}`} />
                  {weeks.map((_, wi) => (
                    <td key={wi} className="px-2 py-2.5 text-center text-sm text-purple-700 dark:text-purple-300 tabular-nums">
                      {(Number(qaPerWeek[wi]) || 0).toFixed(2)}
                    </td>
                  ))}
                </tr>

                {/* Total Capacity row */}
                <tr className="border-t-2 border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-900 font-semibold">
                  <td colSpan={2} className="sticky left-0 z-20 bg-slate-100 dark:bg-slate-900 px-4 py-3 text-sm text-slate-700 dark:text-slate-200">
                    Total Capacity
                  </td>
                  <td className="sticky left-[240px] z-20 bg-slate-100 dark:bg-slate-900 px-4 py-3 text-right text-sm text-slate-800 dark:text-slate-100 tabular-nums">
                    {(Number(totalProjectCapacity) || 0).toFixed(2)}
                  </td>
                  <td className={`sticky left-[340px] z-20 w-[88px] bg-slate-100 dark:bg-slate-900 px-2 py-3 ${shadowClass}`} />
                  {weeks.map((_, wi) => (
                    <td key={wi} className="px-2 py-3 text-center text-sm text-slate-800 dark:text-slate-100 tabular-nums">
                      {(Number(perWeek[wi]) || 0).toFixed(2)}
                    </td>
                  ))}
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      <ModalDialog
        open={!!devToRemove}
        title="Remove team member"
        description={
          devToRemove ? (
            <>
              Are you sure you want to remove <strong>{devToRemove.name}</strong> from this project? This action cannot be undone.
            </>
          ) : (
            ''
          )
        }
        confirmLabel="Remove"
        cancelLabel="Cancel"
        variant="danger"
        loading={removing}
        onConfirm={async () => {
          await onRemove?.(devToRemove.id);
          setDevToRemove(null);
        }}
        onClose={() => setDevToRemove(null)}
      />
    </>
  );
}
